/**
 * Paragraph-only resource metadata: `data-resource-slots="[first:::resourceType,...]"`.
 * For `screenField`, `first` is the field id (`value`); for `advancedTrigger`, `first` is
 * `triggerName`; otherwise it matches `{first}` in text.
 */
import { mergeAttributes } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";

export const RESOURCE_SLOT_DELIM = ":::" as const;

/** First segment is field id for `screenField`, triggerName for `advancedTrigger`; else `{…}` inner text. */
export function encodeResourceSlotToken(braceInner: string, resourceType: string): string {
	return `${braceInner}${RESOURCE_SLOT_DELIM}${resourceType}`;
}

export function decodeResourceSlotToken(token: string): { braceInner: string; resourceType: string } | null {
	const idx = token.indexOf(RESOURCE_SLOT_DELIM);
	if (idx === -1) {
		return null;
	}
	return {
		braceInner: token.slice(0, idx),
		resourceType: token.slice(idx + RESOURCE_SLOT_DELIM.length),
	};
}

/** Serializes for the HTML attribute (inner brackets only; outer quotes come from the DOM). */
export function formatResourceSlotsAttr(slots: string[]): string {
	return `[${slots.join(",")}]`;
}

/**
 * Parses `data-resource-slots`: prefers `[a:::b,c:::d]`; falls back to legacy JSON arrays.
 */
export function parseResourceSlotsAttr(raw: string | null): string[] {
	if (!raw?.trim()) {
		return [];
	}
	const s = raw.trim();

	if (s.startsWith("[") && s.endsWith("]")) {
		const inner = s.slice(1, -1).trim();
		const looksLikeJsonStrings = /^\s*"/.test(inner) || inner.includes("\",\"");
		if (!looksLikeJsonStrings && inner.length > 0) {
			const tokens = inner
				.split(",")
				.map((t) => t.trim())
				.filter(Boolean);
			return [...new Set(tokens)];
		}
	}

	try {
		const parsed = JSON.parse(s) as unknown;
		return normalizeParsedSlotArray(parsed);
	} catch {
		return [];
	}
}

/** @deprecated Use {@link parseResourceSlotsAttr}. */
export const parseResourceSlotsJson = parseResourceSlotsAttr;

function normalizeParsedSlotArray(parsed: unknown): string[] {
	if (!Array.isArray(parsed)) {
		return [];
	}
	const out: string[] = [];
	for (const item of parsed) {
		if (typeof item === "string") {
			out.push(item);
		} else if (
			item &&
      typeof item === "object" &&
      "resourceType" in item &&
      "resourceName" in item
		) {
			const o = item as { resourceType: string; resourceName: string };
			out.push(encodeResourceSlotToken(o.resourceName, o.resourceType));
		}
	}
	return [...new Set(out)];
}

export const ParagraphResourceMeta = Paragraph.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			resourceSlots: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-resource-slots"),
				renderHTML: (attributes) => {
					if (!attributes.resourceSlots) {
						return {};
					}
					return { "data-resource-slots": attributes.resourceSlots };
				},
			},
		};
	},

	renderHTML({ HTMLAttributes }) {
		return ["p", mergeAttributes(this.options.HTMLAttributes ?? {}, HTMLAttributes), 0];
	},
});
